import React, {useEffect, useMemo, useRef, useState} from "react";

import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {AutoComplete} from "primereact/autocomplete";

import {Loading, IndexTemplate} from "../../utils/InlineComponents";
import {AppContext} from "../../HomePage";
import {CampaignQualityService} from "../../service/CampaignQualityService";
import AdminPageHeader from "./AdminPageHeader";


// Ordered dimension list shared by list mini-bars and the detail breakdown.
// Keys MUST match the backend subscores dict (compute_subscores).
const DIMENSIONS = [
    {key: "matching",            labelKey: "campaign_quality.dimension.matching"},
    {key: "data_hygiene",        labelKey: "campaign_quality.dimension.data_hygiene"},
    {key: "stock_url_validity",  labelKey: "campaign_quality.dimension.stock_url_validity"},
    {key: "coverage",            labelKey: "campaign_quality.dimension.coverage"},
    {key: "freshness",           labelKey: "campaign_quality.dimension.freshness"},
    {key: "scrape_reliability",  labelKey: "campaign_quality.dimension.scrape_reliability"},
    {key: "setup",               labelKey: "campaign_quality.dimension.setup"},
];

// Grade -> color. Worst grades are visually loud.
const gradeColor = (grade) => {
    if (grade === "A") return "#22c55e";
    if (grade === "B") return "#84cc16";
    if (grade === "C") return "#f59e0b";
    return "#ef4444"; // D / unknown
};

// Score (0-100) -> color, same scale as grades for consistency.
const scoreColor = (score) => {
    if (score >= 85) return "#22c55e";
    if (score >= 70) return "#84cc16";
    if (score >= 50) return "#f59e0b";
    return "#ef4444";
};

const CampaignQuality = (props) => {

    const {t, toast, setError} = React.useContext(AppContext);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkRecomputing, setBulkRecomputing] = useState(false);

    // Filters
    const [accountType, setAccountType] = useState(null);
    const [usageType, setUsageType] = useState(null);
    const [grade, setGrade] = useState(null);
    const [query, setQuery] = useState("");

    // Customer picker (admin-mode) — multi-select; each entry is a customer object.
    const [customers, setCustomers] = useState([]);
    const [customerSuggestions, setCustomerSuggestions] = useState([]);
    const allUsersRef = useRef([]);

    // In-page detail swap (mirrors Product list -> detail): when set, the
    // detail view is rendered instead of the table; no route change.
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [recomputing, setRecomputing] = useState(false);

    const campaignQualityService = new CampaignQualityService();

    useEffect(() => {
        // Load only active-subscription customers for the customer picker.
        campaignQualityService.customers().then(customers => {
            allUsersRef.current = customers || [];
        }).catch(() => {});
        search();
    }, []);

    // search is called either with the current customers state or with an explicit
    // override array (needed so callers can pass the freshly-computed array rather
    // than reading the stale state captured in their closure).
    const search = (customersOverride) => {
        const activeCustomers = customersOverride !== undefined ? customersOverride : customers;
        setLoading(true);
        const fetcher = activeCustomers.length > 0
            ? campaignQualityService.campaignsForUsers(activeCustomers.map(c => c.id))
            : campaignQualityService.list({
                account_type: accountType || undefined,
                usage_type: usageType || undefined,
                grade: grade || undefined,
                q: query || undefined,
                sort: "overall",
            });
        fetcher.then(data => {
            setRows(data || []);
            setLoading(false);
        }).catch(error => {
            setError(error);
            setError(null);
            setLoading(false);
        });
    };

    const openDetail = (rowData) => {
        // In customer mode, rows without a score have no snapshot to retrieve;
        // block the detail navigation and show an informational toast instead.
        if (rowData.has_score === false) {
            toast.current.show({severity: 'info', summary: t('campaign_quality.no_score_yet')});
            return;
        }
        setSelected(rowData);
        setDetail(null);
        setDetailLoading(true);
        campaignQualityService.detail(rowData.campaign_id).then(data => {
            setDetail(data);
            setDetailLoading(false);
        }).catch(error => {
            setError(error);
            setError(null);
            setDetailLoading(false);
        });
    };

    // AutoComplete completeMethod: filter active customers by email or name,
    // excluding those already selected so they don't appear twice in suggestions.
    const completeCustomer = (event) => {
        const q = (event.query || "").toLowerCase();
        const alreadySelected = new Set(customers.map(c => c.id));
        const pool = allUsersRef.current.filter(u => !alreadySelected.has(u.id));
        if (!q) {
            setCustomerSuggestions(pool.slice(0, 50));
            return;
        }
        setCustomerSuggestions(
            pool.filter(u =>
                u.email.toLowerCase().indexOf(q) !== -1 ||
                (u.name || "").toLowerCase().indexOf(q) !== -1
            ).slice(0, 50)
        );
    };

    // Called whenever the AutoComplete value changes (add chip or remove chip).
    // newCustomers is the updated array; we pass it directly to search() to avoid
    // reading the stale customers state captured at call time.
    const onCustomersChange = (newCustomers) => {
        // PrimeReact multiple AutoComplete may emit mixed arrays when the user
        // types mid-selection; keep only proper customer objects.
        const filtered = (newCustomers || []).filter(c => c && typeof c === 'object' && c.id);
        setCustomers(filtered);
        search(filtered);
    };

    const backToList = () => {
        setSelected(null);
        setDetail(null);
    };

    const recompute = () => {
        if (!selected) return;
        setRecomputing(true);
        campaignQualityService.recompute(selected.campaign_id).then(() => {
            setRecomputing(false);
            toast.current.show({severity: 'success', summary: t('campaign_quality.recompute'), detail: t('message_detail.successful')});
            openDetail(selected);
            search();
        }).catch(error => {
            setRecomputing(false);
            setError(error);
            setError(null);
        });
    };

    const bulkRecomputeAll = () => {
        setBulkRecomputing(true);
        // Capture current customers so the delayed refresh uses the same selection.
        const currentCustomers = customers;
        campaignQualityService.bulkRecompute([]).then(() => {
            setBulkRecomputing(false);
            toast.current.show({severity: 'success', summary: t('campaign_quality.recompute'), detail: t('campaign_quality.recompute_queued')});
            setSelectedRows([]);
            setTimeout(() => search(currentCustomers), 4000);
        }).catch(error => {
            setBulkRecomputing(false);
            setError(error);
            setError(null);
        });
    };

    const bulkRecomputeSelected = () => {
        if (!selectedRows.length) return;
        setBulkRecomputing(true);
        // Capture current customers so the delayed refresh uses the same selection.
        const currentCustomers = customers;
        campaignQualityService.bulkRecompute(selectedRows.map(r => r.campaign_id)).then(() => {
            setBulkRecomputing(false);
            toast.current.show({severity: 'success', summary: t('campaign_quality.recompute'), detail: t('campaign_quality.recompute_queued')});
            setSelectedRows([]);
            setTimeout(() => search(currentCustomers), 4000);
        }).catch(error => {
            setBulkRecomputing(false);
            setError(error);
            setError(null);
        });
    };

    const accountTypeOptions = [
        {value: "paid",  label: t('campaign_quality.account_type.paid')},
        {value: "trial", label: t('campaign_quality.account_type.trial')},
    ];
    const gradeOptions = [
        {value: "A", label: "A"},
        {value: "B", label: "B"},
        {value: "C", label: "C"},
        {value: "D", label: "D"},
    ];

    // Distinct usage types observed in the loaded rows (the backend defines the
    // vocabulary; we offer what is present rather than hard-coding it).
    const usageTypeOptions = useMemo(() => {
        const seen = [];
        rows.forEach(r => {
            if (r.usage_type && !seen.includes(r.usage_type)) seen.push(r.usage_type);
        });
        return seen.map(u => ({value: u, label: u}));
    }, [rows]);

    // ---- list cell templates ----

    const customerTemplate = (rowData) => <span>{rowData.user_email || rowData.user_id}</span>;

    const accountTypeTemplate = (rowData) => <span>{t('campaign_quality.account_type.' + rowData.account_type, rowData.account_type)}</span>;

    const overallTemplate = (rowData) => {
        if (rowData.overall == null) {
            return <span style={{color: 'var(--text-color-secondary)', fontStyle: 'italic'}}>{t('campaign_quality.not_computed')}</span>;
        }
        return (
            <span style={{fontWeight: 600}}>
                {rowData.overall}
                <span style={{
                    marginLeft: '0.5em', padding: '0.1em 0.5em', borderRadius: '4px',
                    color: '#fff', backgroundColor: gradeColor(rowData.grade), fontSize: '0.85em'
                }}>{rowData.grade}</span>
            </span>
        );
    };

    // Seven thin stacked bars, one per dimension, colored by score.
    const subscoresTemplate = (rowData) => (
        <div className="p-grid p-align-center" style={{margin: 0}}>
            {DIMENSIONS.map(dim => {
                const v = (rowData.subscores && rowData.subscores[dim.key] != null) ? rowData.subscores[dim.key] : 0;
                return (
                    <div key={dim.key}
                         title={t(dim.labelKey) + ': ' + v}
                         style={{
                             width: '14px', height: '34px', marginRight: '3px',
                             background: 'var(--surface-d, #e0e0e0)', borderRadius: '2px',
                             display: 'flex', alignItems: 'flex-end', overflow: 'hidden'
                         }}>
                        <div style={{width: '100%', height: v + '%', background: scoreColor(v)}}/>
                    </div>
                );
            })}
        </div>
    );

    const churnTemplate = (rowData) => {
        const churn = rowData.churn || {};
        if (churn.trial_expiring_soon) {
            return <span style={{
                padding: '0.15em 0.6em', borderRadius: '10px', color: '#fff',
                backgroundColor: '#ef4444', fontSize: '0.8em'
            }}>{t('campaign_quality.churn.trial_expiring', {days: churn.trial_days_left})}</span>;
        }
        const band = churn.login_band || 'active';
        const bandColor = (band === 'at_risk' || band === 'never') ? '#ef4444' : (band === 'slipping' ? '#f59e0b' : '#22c55e');
        return <span style={{
            padding: '0.15em 0.6em', borderRadius: '10px', color: '#fff',
            backgroundColor: bandColor, fontSize: '0.8em'
        }}>{t('campaign_quality.churn.band.' + band, band)}</span>;
    };

    const topRecommendationTemplate = (rowData) => {
        const recs = rowData.recommendations || [];
        if (!recs.length) return <span style={{color: 'var(--text-color-secondary)'}}>{t('campaign_quality.no_recommendations')}</span>;
        const top = recs[0];
        return <span title={top.title}>{top.title} <span style={{color: '#22c55e'}}>(+{top.estimated_delta})</span></span>;
    };

    const computedAtTemplate = (rowData) => <span>{rowData.computed_at ? new Date(rowData.computed_at).toLocaleString() : '-'}</span>;

    // ---- detail sub-renderers ----

    // Circular score gauge (inline SVG, no chart dependency).
    const renderGauge = (overall, gradeValue) => {
        const radius = 52;
        const circumference = 2 * Math.PI * radius;
        const offset = circumference * (1 - (overall || 0) / 100);
        return (
            <svg width="140" height="140" viewBox="0 0 140 140">
                <circle cx="70" cy="70" r={radius} fill="none" stroke="var(--surface-d, #e0e0e0)" strokeWidth="12"/>
                <circle cx="70" cy="70" r={radius} fill="none" stroke={scoreColor(overall)} strokeWidth="12"
                        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
                        transform="rotate(-90 70 70)"/>
                <text x="70" y="66" textAnchor="middle" fontSize="30" fontWeight="700" fill="var(--text-color)">{overall}</text>
                <text x="70" y="92" textAnchor="middle" fontSize="18" fontWeight="600" fill={gradeColor(gradeValue)}>{gradeValue}</text>
            </svg>
        );
    };

    // Horizontal dimension breakdown bars.
    const renderDimensionBreakdown = (subscores) => (
        <div>
            {DIMENSIONS.map(dim => {
                const v = (subscores && subscores[dim.key] != null) ? subscores[dim.key] : 0;
                return (
                    <div key={dim.key} style={{marginBottom: '0.6em'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.85em'}}>
                            <span>{t(dim.labelKey)}</span>
                            <span style={{fontWeight: 600}}>{v}</span>
                        </div>
                        <div style={{height: '8px', background: 'var(--surface-d, #e0e0e0)', borderRadius: '4px', overflow: 'hidden'}}>
                            <div style={{width: v + '%', height: '100%', background: scoreColor(v)}}/>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    // Delta-sorted recommendation list with deep-links (rows already sorted by
    // backend; we render in given order).
    const renderRecommendations = (recs) => {
        if (!recs || !recs.length) return <p style={{color: 'var(--text-color-secondary)'}}>{t('campaign_quality.no_recommendations')}</p>;
        return (
            <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                {recs.map((rec, idx) => (
                    <li key={idx} style={{padding: '0.6em 0', borderBottom: '1px solid var(--surface-d, #e0e0e0)'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <span style={{fontWeight: 600}}>
                                {rec.deep_link
                                    ? <a href={rec.deep_link} style={{color: 'var(--primary-color)'}}>{rec.title}</a>
                                    : rec.title}
                            </span>
                            <span style={{color: '#22c55e', fontWeight: 600}}>+{rec.estimated_delta}</span>
                        </div>
                        <div style={{fontSize: '0.8em', color: 'var(--text-color-secondary)'}}>
                            {t('campaign_quality.dimension.' + rec.dimension, rec.dimension)}
                            {' · '}{t('campaign_quality.severity.' + rec.severity, rec.severity)}
                            {rec.affected_count != null && (' · ' + t('campaign_quality.affected', {count: rec.affected_count}))}
                        </div>
                    </li>
                ))}
            </ul>
        );
    };

    // Churn axis: login recency + trial countdown rendered as a labeled strip.
    const renderChurnAxis = (churn) => {
        const c = churn || {};
        const band = c.login_band || 'active';
        const bandColor = (band === 'at_risk' || band === 'never') ? '#ef4444' : (band === 'slipping' ? '#f59e0b' : '#22c55e');
        return (
            <div>
                <div style={{display: 'flex', alignItems: 'center', marginBottom: '0.4em'}}>
                    <span style={{
                        width: '12px', height: '12px', borderRadius: '50%',
                        background: bandColor, marginRight: '0.5em'
                    }}/>
                    <span>{t('campaign_quality.churn.login_recency', {days: c.login_recency_days})} — {t('campaign_quality.churn.band.' + band, band)}</span>
                </div>
                {c.trial_days_left != null && (
                    <div style={{color: c.trial_expiring_soon ? '#ef4444' : 'var(--text-color)'}}>
                        {t('campaign_quality.churn.trial_left', {days: c.trial_days_left})}
                    </div>
                )}
            </div>
        );
    };

    // Trend sparkline (inline SVG polyline of overall over time).
    const renderTrend = (trend) => {
        if (!trend || trend.length < 2) return <p style={{color: 'var(--text-color-secondary)'}}>{t('campaign_quality.no_trend')}</p>;
        const w = 280, h = 60, pad = 4;
        const xs = (i) => pad + (i * (w - 2 * pad)) / (trend.length - 1);
        const ys = (v) => h - pad - ((v || 0) / 100) * (h - 2 * pad);
        const points = trend.map((pt, i) => xs(i) + ',' + ys(pt.overall)).join(' ');
        return (
            <svg width={w} height={h} viewBox={'0 0 ' + w + ' ' + h}>
                <polyline fill="none" stroke="var(--primary-color)" strokeWidth="2" points={points}/>
                {trend.map((pt, i) => <circle key={i} cx={xs(i)} cy={ys(pt.overall)} r="2.5" fill="var(--primary-color)"/>)}
            </svg>
        );
    };

    // ---- detail view ----

    const renderDetail = () => {
        const d = detail || {};
        return (
            <React.Fragment>
                <AdminPageHeader
                    title={t('menu_item.campaign_quality')}
                    subtitle={(selected && (selected.user_email || selected.user_id)) + ' · ' + (selected && (selected.campaign_name || selected.campaign_id))}
                    actions={<>
                        <Button label={t('actions.back')} icon="pi pi-arrow-left" className="p-button-outlined p-mr-2"
                                onClick={backToList} style={{marginRight: '0.5em'}}/>
                        <Button label={t('campaign_quality.recompute')} icon="pi pi-refresh" className="p-button-success"
                                loading={recomputing} disabled={recomputing} onClick={recompute}/>
                    </>}
                />
                {detailLoading ?
                    <div className="p-col-12" style={{textAlign: 'center'}}><Loading/></div> :
                    <div className="p-grid">
                        <div className="p-col-12 p-md-4">
                            <div className="card" style={{textAlign: 'center'}}>
                                <h5>{t('campaign_quality.overall')}</h5>
                                {renderGauge(d.overall, d.grade)}
                            </div>
                            <div className="card">
                                <h5>{t('campaign_quality.churn.title')}</h5>
                                {renderChurnAxis(d.churn)}
                            </div>
                            <div className="card">
                                <h5>{t('campaign_quality.trend')}</h5>
                                {renderTrend(d.trend)}
                            </div>
                        </div>
                        <div className="p-col-12 p-md-4">
                            <div className="card">
                                <h5>{t('campaign_quality.breakdown')}</h5>
                                {renderDimensionBreakdown(d.subscores)}
                            </div>
                        </div>
                        <div className="p-col-12 p-md-4">
                            <div className="card">
                                <h5>{t('campaign_quality.recommendations')}</h5>
                                {renderRecommendations(d.recommendations)}
                            </div>
                        </div>
                    </div>}
            </React.Fragment>
        );
    };

    // ---- list view ----

    const renderList = () => (
        <React.Fragment>
            <AdminPageHeader
                title={t('menu_item.campaign_quality')}
                subtitle={t('menu.management_hub.page.campaign_quality')}
                actions={<>
                    <Button label={t('actions.refresh')} icon="pi pi-refresh" className="p-button-outlined p-mr-2"
                            onClick={() => search()} disabled={loading} style={{marginRight: '0.5em'}}/>
                    <Button label={t('campaign_quality.recompute_all')} icon="pi pi-sync" className="p-button-outlined p-mr-2"
                            onClick={bulkRecomputeAll} disabled={bulkRecomputing} style={{marginRight: '0.5em'}}/>
                    <Button label={t('campaign_quality.recompute_selected')} icon="pi pi-check-square" className="p-button-outlined"
                            onClick={bulkRecomputeSelected} disabled={bulkRecomputing || !selectedRows.length}/>
                </>}
            />

            <div className="card">
                <div className="p-grid p-fluid p-align-end">
                    <div className="p-col-12 p-md-3">
                        <label htmlFor="customer_filter">{t('campaign_quality.customer_filter')}</label>
                        <AutoComplete
                            id="customer_filter"
                            multiple
                            value={customers}
                            suggestions={customerSuggestions}
                            completeMethod={completeCustomer}
                            field="email"
                            itemTemplate={(item) => {
                                const accountTypeBgColor = item.account_type === 'paid' ? '#22c55e' : item.account_type === 'trial' ? '#f59e0b' : '#9ca3af';
                                return (
                                    <span>
                                        {item.email}{' '}
                                        <span style={{color:'var(--text-color-secondary)', fontSize:'0.85em'}}>{item.name}</span>
                                        {item.account_type && (
                                            <span style={{
                                                marginLeft:'0.5em', padding:'0.1em 0.5em', borderRadius:'4px',
                                                color:'#fff', backgroundColor: accountTypeBgColor, fontSize:'0.8em'
                                            }}>{t('campaign_quality.account_type.' + item.account_type, item.account_type)}</span>
                                        )}
                                    </span>
                                );
                            }}
                            onChange={(e) => onCustomersChange(e.value)}
                            placeholder={customers.length === 0 ? t('actions.all') : ''}
                            dropdown
                            style={{width:'100%'}}
                        />
                    </div>
                    <div className="p-col-12 p-md-2">
                        <label htmlFor="account_type">{t('campaign_quality.account_type.label')}</label>
                        <Dropdown id="account_type" value={accountType} options={accountTypeOptions}
                                  onChange={(e) => setAccountType(e.value)}
                                  placeholder={t('actions.all')} showClear/>
                    </div>
                    <div className="p-col-12 p-md-2">
                        <label htmlFor="usage_type">{t('campaign_quality.usage_type')}</label>
                        <Dropdown id="usage_type" value={usageType} options={usageTypeOptions}
                                  onChange={(e) => setUsageType(e.value)}
                                  placeholder={t('actions.all')} showClear/>
                    </div>
                    <div className="p-col-12 p-md-2">
                        <label htmlFor="grade">{t('campaign_quality.grade')}</label>
                        <Dropdown id="grade" value={grade} options={gradeOptions}
                                  onChange={(e) => setGrade(e.value)}
                                  placeholder={t('actions.all')} showClear/>
                    </div>
                    <div className="p-col-12 p-md-2">
                        <label htmlFor="q">{t('campaign_quality.search')}</label>
                        <InputText id="q" value={query} onChange={(e) => setQuery(e.target.value)}
                                   onKeyDown={(e) => { if (e.key === 'Enter') search(); }}/>
                    </div>
                    <div className="p-col-12 p-md-1">
                        <Button label={t('actions.search')} icon="pi pi-search" className="p-button-primary"
                                onClick={() => search()} disabled={loading}/>
                    </div>
                </div>
            </div>

            {loading ?
                <div className="p-col-12" style={{textAlign: 'center'}}>
                    <Loading/>
                </div> :
                <DataTable value={rows}
                           emptyMessage={t('actions.empty_message')}
                           sortField="overall" sortOrder={1}
                           paginator={true} rows={100}
                           onRowClick={(e) => {
                               // In customer mode: block detail for unscored rows.
                               // In snapshot mode: has_score is always truthy (backend only
                               // returns scored campaigns), so the guard is a safe no-op.
                               openDetail(e.data);
                           }}
                           rowHover
                           selection={selectedRows}
                           onSelectionChange={(e) => setSelectedRows(e.value)}
                           dataKey="campaign_id">
                    <Column selectionMode="multiple" headerStyle={{width: '3em'}} onClick={(e) => e.stopPropagation()}/>
                    <Column body={IndexTemplate} headerStyle={{width: '3em'}}/>
                    <Column field="user_email" body={customerTemplate} header={t('campaign_quality.customer')} sortable/>
                    <Column field="campaign_name" body={(rowData) => <span>{rowData.campaign_name || rowData.campaign_id}</span>} header={t('campaign_quality.campaign')} sortable/>
                    <Column field="account_type" body={accountTypeTemplate} header={t('campaign_quality.account_type.label')} sortable/>
                    <Column field="overall" body={overallTemplate} header={t('campaign_quality.overall')} sortable/>
                    <Column body={subscoresTemplate} header={t('campaign_quality.subscores')}/>
                    <Column body={churnTemplate} header={t('campaign_quality.churn.title')}/>
                    <Column body={topRecommendationTemplate} header={t('campaign_quality.top_recommendation')}/>
                    <Column field="computed_at" body={computedAtTemplate} header={t('campaign_quality.computed_at')} sortable/>
                </DataTable>}
        </React.Fragment>
    );

    return (
        <div className="card management-page">
            {selected ? renderDetail() : renderList()}
        </div>
    );
};
export default CampaignQuality;
